import React, { useState, useEffect } from 'react';
import {
    Paper,
    Grid,
    Typography,
    Card,
    TextField,
    Button,
    MenuItem,
    Checkbox,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    CircularProgress,
    Chip,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Box
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WarningIcon from '@mui/icons-material/Warning';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import { useSnackbar } from 'notistack';
import { honorariosAuditoriaJWT, honorariosAuditoriaAuditarJWT, honorariosAuditoriaObsJWT } from '../../api/endpoints';
import SyncIcon from '@mui/icons-material/Sync';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import LayersIcon from '@mui/icons-material/Layers';
import moment from 'moment';
import DetallePlanDrawer from '../../components/DetallePlanDrawer';

const sxStatCard = {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    p: 2,
    color: 'white',
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
    },
    '&:active': {
        transform: 'translateY(-2px)',
    }
};

/**
 * Tabla de Auditoría — se usa desde dos lugares:
 *   - Embebida en HonorariosAdminHome (modo legacy, con sus stat cards arriba).
 *   - Embebida en AuditoriaPage (modulo nuevo independiente con pipeline +
 *     atencion arriba, por eso `modoEmbebido=true` oculta las stat cards
 *     internas para evitar duplicacion).
 *
 * Props opcionales:
 *   - modoEmbebido: oculta las stat cards y margenes externos.
 *   - onStatsChange: callback con stats actualizados (para que el padre los
 *     muestre en su propio pipeline).
 *   - onFiltersChange: callback con filtros actuales (para el padre).
 *   - filtroEstadoExterno: si viene, fuerza el filtro de estado desde el padre
 *     (para click en cards del pipeline).
 */
export default function AuditoriaAdminTable({
    modoEmbebido = false,
    onStatsChange,
    onFiltersChange,
    filtroEstadoExterno,
} = {}) {
    const { enqueueSnackbar } = useSnackbar(); // NOTISTACK HOOK
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [stats, setStats] = useState({ auditado: 0, pendiente: 0, incompleto: 0 });
    const [selected, setSelected] = useState([]);

    // Confirmation Dialog State
    const [confirmOpen, setConfirmOpen] = useState(false);

    // Super-drawer "Detalle completo del plan"
    const [planDetalleId, setPlanDetalleId] = useState(null);

    // Observaciones Dialog State
    const [obsOpen, setObsOpen] = useState(false);
    const [obsData, setObsData] = useState({ id: null, referencia: '', protocolo: '', autos: '' });
    const [obsSaving, setObsSaving] = useState(false);

    // Filters
    const defaultFilters = {
        desde: moment().subtract(1, 'month').format('YYYY-MM-DD'),
        hasta: moment().format('YYYY-MM-DD'),
        estado: '0', // Default: Pendientes
        texto: ''
    };
    const [filters, setFilters] = useState(defaultFilters);

    // Si el padre cambia el filtro de estado (click en pipeline), lo aplicamos.
    useEffect(() => {
        if (filtroEstadoExterno !== undefined && filtroEstadoExterno !== filters.estado) {
            const next = { ...filters, estado: filtroEstadoExterno };
            setFilters(next);
            fetchData(next);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtroEstadoExterno]);

    // Notificamos al padre los filtros (para que sepa cuál está activo).
    useEffect(() => {
        if (onFiltersChange) onFiltersChange(filters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    // Filtro cruzamiento (client-side)
    const [filtroCruzamiento, setFiltroCruzamiento] = useState('todos'); // 'todos', 'coincide', 'sin_match', 'sin_cruzar'

    // Pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);

    const fetchData = async (currentFilters = filters) => {
        setLoading(true);
        setSelected([]); // Clear selection on reload
        try {
            const fechaDesde = currentFilters.desde || undefined;
            const fechaHasta = currentFilters.hasta || undefined;
            const estado = currentFilters.estado !== '100' ? currentFilters.estado : undefined;
            let filtro = undefined;
            if (currentFilters.texto) {
                // Si ingresan algo como E-201120509, usamos solo el numero para que matchee en la DB
                const certMatch = currentFilters.texto.match(/^[A-Za-z]?\s*[- ]?\s*([0-9]+)$/);
                filtro = certMatch ? certMatch[1] : currentFilters.texto;
            }

            const response = await honorariosAuditoriaJWT(fechaDesde, fechaHasta, estado, filtro);
            if (response.ok) {
                setData(response.data || []);
                const newStats = response.stats || { auditado: 0, pendiente: 0, incompleto: 0 };
                setStats(newStats);
                if (onStatsChange) onStatsChange(newStats);
            } else {
                enqueueSnackbar(response.error || 'No se pudieron cargar los datos', { variant: 'error' });
            }
        } catch (error) {
            enqueueSnackbar('Error de conexión al obtener datos', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // El antiguo handler "Cruzar Datos" se removió de esta pantalla.
    // La vinculación de planes Incompletos a expedientes existentes ahora se
    // hace automáticamente cada noche con el cron de DGR, o manualmente desde
    // el botón "Vincular ahora" en la pantalla de Planes (DGR).

    const handleFilterChange = (prop) => (event) => {
        setFilters({ ...filters, [prop]: event.target.value });
    };

    const runSearch = (nextFilters) => {
        setFilters(nextFilters);
        setPage(0);
        fetchData(nextFilters);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            const reset = { ...defaultFilters };
            setSelected([]);
            runSearch(reset);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            const merged = e.target?.name ? { ...filters, [e.target.name]: e.target.value } : { ...filters };
            runSearch(merged);
        }
    };

    const handleSearch = () => {
        runSearch({ ...filters });
    };


    const handleCardClick = (estado) => {
        const nuevoEstado = filters.estado === estado ? '100' : estado;
        const newFilters = { ...filters, estado: nuevoEstado };
        setFilters(newFilters);
        setPage(0);
        fetchData(newFilters);
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            const newSelecteds = data.filter(n => n.auditado !== 1).map((n) => n.id);
            setSelected(newSelecteds);
        } else {
            setSelected([]);
        }
    };

    const handleSelect = (event, id) => {
        const selectedIndex = selected.indexOf(id);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1),
            );
        }
        setSelected(newSelected);
    };

    // Open Confirmation Dialog
    const handleMassAuditClick = () => {
        if (selected.length === 0) return;
        setConfirmOpen(true);
    };

    // Execute Audit
    const handleMassAuditConfirm = async () => {
        setConfirmOpen(false);
        setLoading(true);
        try {
            const response = await honorariosAuditoriaAuditarJWT(selected);

            if (response.ok) {
                enqueueSnackbar(response.mensaje || 'Registros auditados correctamente', { variant: 'success' });
                fetchData(filters);
            } else {
                enqueueSnackbar(response.error || 'Error al auditar registros', { variant: 'error' });
            }
        } catch (error) {
            enqueueSnackbar('Error de conexión al auditar', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const openExpediente = (row) => {
        if (!row.expediente_id || row.expediente_id === 0) {
            enqueueSnackbar('El registro no tiene expediente asignado', { variant: 'warning' });
            return;
        }

        // Determinar tipo de expediente: 1=judicial, 2=administrativo
        const tipoPath = row.expediente_tipo === 1 ? 'judicial' : 'administrativo';
        const url = `/expediente/${tipoPath}/${row.expediente_id}`;

        // Abrir en nueva ventana
        window.open(url, '_blank');
    };

    const openObs = (row) => {
        if (!row.expediente_id || row.expediente_id === 0) {
            enqueueSnackbar('El registro no tiene expediente asignado', { variant: 'warning' });
            return;
        }
        // Abrir dialog de observaciones en React
        setObsData({
            id: row.id,
            referencia: row.referencia || '',
            protocolo: row.protocolo || '',
            autos: row.autos || ''
        });
        setObsOpen(true);
    };

    const handleObsClose = () => {
        setObsOpen(false);
        setObsData({ id: null, referencia: '', protocolo: '', autos: '' });
    };

    const handleObsSave = async () => {
        if (!obsData.referencia.trim()) {
            enqueueSnackbar('La referencia es obligatoria', { variant: 'warning' });
            return;
        }

        setObsSaving(true);
        try {
            const response = await honorariosAuditoriaObsJWT(
                obsData.id,
                obsData.referencia,
                obsData.protocolo
            );

            if (response.ok) {
                enqueueSnackbar('Observaciones guardadas correctamente', { variant: 'success' });
                handleObsClose();
                fetchData(); // Reload data
            } else {
                enqueueSnackbar(response.error || 'Error al guardar observaciones', { variant: 'error' });
            }
        } catch (error) {
            enqueueSnackbar('Error de conexión al guardar', { variant: 'error' });
        } finally {
            setObsSaving(false);
        }
    };

    return (
        <Box sx={{
            width: '100%',
            maxWidth: '100%',
            mt: modoEmbebido ? 0 : 2,
            px: modoEmbebido ? 0 : 2,
            py: modoEmbebido ? 0 : 1,
            overflowX: 'hidden',
            boxSizing: 'border-box',
        }}>
            {/* Statistics Cards — ocultas en modo embebido (el padre las muestra como pipeline) */}
            {!modoEmbebido && (
            <Grid container spacing={3} style={{ marginBottom: 24 }}>
                <Grid size={{ xs: 12, md: 4 }}>
                    <Card
                        sx={{
                            ...sxStatCard,
                            background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                            ...(filters.estado === '1' ? { boxShadow: '0 0 0 3px #2e7d32', transform: 'translateY(-4px)' } : {}),
                        }}
                        onClick={() => handleCardClick('1')}
                    >
                        <Typography variant="h5" style={{ fontWeight: 700 }}>{stats.auditado}</Typography>
                        <Typography variant="subtitle2">Filas Auditadas</Typography>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <Card
                        sx={{
                            ...sxStatCard,
                            background: 'linear-gradient(45deg, #FF9800 30%, #FFB74D 90%)',
                            ...(filters.estado === '-1' ? { boxShadow: '0 0 0 3px #2e7d32', transform: 'translateY(-4px)' } : {}),
                        }}
                        onClick={() => handleCardClick('-1')}
                    >
                        <Typography variant="h5" style={{ fontWeight: 700 }}>{stats.incompleto}</Typography>
                        <Typography variant="subtitle2">Filas Incompletas (Sin cruce)</Typography>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <Card
                        sx={{
                            ...sxStatCard,
                            background: '#E0E0E0',
                            color: '#333',
                            ...(filters.estado === '0' ? { boxShadow: '0 0 0 3px #2e7d32', transform: 'translateY(-4px)' } : {}),
                        }}
                        onClick={() => handleCardClick('0')}
                    >
                        <Typography variant="h5" style={{ fontWeight: 700 }}>{stats.pendiente}</Typography>
                        <Typography variant="subtitle2">Filas Pendientes (Listas para auditar)</Typography>
                    </Card>
                </Grid>
            </Grid>
            )}
            <Dialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">{"¿Confirmar Auditoría?"}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Se marcarán como auditados {selected.length} registros seleccionados. Esta acción no se puede deshacer fácilmente.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)} color="primary">
                        Cancelar
                    </Button>
                    <Button onClick={handleMassAuditConfirm} color="primary" autoFocus>
                        Sí, Auditar
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Observaciones Dialog */}
            <Dialog
                open={obsOpen}
                onClose={handleObsClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Observaciones - {obsData.autos}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Referencia (obligatorio)"
                        fullWidth
                        value={obsData.referencia}
                        onChange={(e) => setObsData({ ...obsData, referencia: e.target.value })}
                        variant="outlined"
                        helperText="Ingrese la referencia del plan de pago"
                    />
                    <TextField
                        margin="dense"
                        label="Protocolo"
                        fullWidth
                        value={obsData.protocolo}
                        onChange={(e) => setObsData({ ...obsData, protocolo: e.target.value })}
                        variant="outlined"
                        style={{ marginTop: 16 }}
                        helperText="Ingrese el protocolo si corresponde"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleObsClose}>
                        Cancelar
                    </Button>
                    <Button onClick={handleObsSave} color="primary" disabled={obsSaving}>
                        {obsSaving ? <CircularProgress size={24} /> : 'Guardar'}
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Filters */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid size={{ xs: 6, md: 2 }}>
                        <TextField
                            label="Desde"
                            type="date"
                            name="desde"
                            value={filters.desde}
                            onChange={handleFilterChange('desde')}
                            InputLabelProps={{ shrink: true }}
                            onKeyDown={handleKeyDown}
                            fullWidth
                        />
                    </Grid>
                    <Grid size={{ xs: 6, md: 2 }}>
                        <TextField
                            label="Hasta"
                            type="date"
                            name="hasta"
                            value={filters.hasta}
                            onChange={handleFilterChange('hasta')}
                            InputLabelProps={{ shrink: true }}
                            onKeyDown={handleKeyDown}
                            fullWidth
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 2 }}>
                        <TextField
                            select
                            label="Estado"
                            name="estado"
                            value={filters.estado}
                            onChange={handleFilterChange('estado')}
                            onKeyDown={handleKeyDown}
                            fullWidth
                        >
                            <MenuItem value="100">Todos</MenuItem>
                            <MenuItem value="1">Auditado</MenuItem>
                            <MenuItem value="0">Pendiente</MenuItem>
                            <MenuItem value="-1">Incompleto</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                            label="Buscar (Certificado, Autos, Abogado...)"
                            name="texto"
                            value={filters.texto}
                            onChange={handleFilterChange('texto')}
                            fullWidth
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleSearch();
                                } else {
                                    handleKeyDown(e);
                                }
                            }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 2 }}>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<SearchIcon />}
                            fullWidth
                            onClick={handleSearch}
                            disabled={loading}
                        >
                            Buscar
                        </Button>
                    </Grid>
                </Grid>
            </Paper>
            {/* Actions Bar */}
            <Paper style={{ padding: 12, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">
                        {selected.length > 0 ? `${selected.length} seleccionados` : 'Seleccione filas para auditar'}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 2 }}>
                        <Chip
                            size="small"
                            icon={<FiberManualRecordIcon sx={{ fontSize: 10, color: '#4CAF50 !important' }} />}
                            label="Coincide"
                            variant={filtroCruzamiento === 'coincide' ? 'filled' : 'outlined'}
                            onClick={() => { setFiltroCruzamiento(f => f === 'coincide' ? 'todos' : 'coincide'); setPage(0); }}
                            sx={{ cursor: 'pointer' }}
                        />
                        <Chip
                            size="small"
                            icon={<FiberManualRecordIcon sx={{ fontSize: 10, color: '#FF9800 !important' }} />}
                            label="Sin cruzar"
                            variant={filtroCruzamiento === 'sin_cruzar' ? 'filled' : 'outlined'}
                            onClick={() => { setFiltroCruzamiento(f => f === 'sin_cruzar' ? 'todos' : 'sin_cruzar'); setPage(0); }}
                            sx={{ cursor: 'pointer' }}
                        />
                        <Chip
                            size="small"
                            icon={<FiberManualRecordIcon sx={{ fontSize: 10, color: '#f44336 !important' }} />}
                            label="Sin match"
                            variant={filtroCruzamiento === 'sin_match' ? 'filled' : 'outlined'}
                            onClick={() => { setFiltroCruzamiento(f => f === 'sin_match' ? 'todos' : 'sin_match'); setPage(0); }}
                            sx={{ cursor: 'pointer' }}
                        />
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {/* Botón "Cruzar Datos" removido — el cron diario hace la
                        vinculación automática cada noche, y si querés forzarla
                        ahora usá "Vincular ahora" en la pantalla de Planes (DGR). */}
                    <Button
                        variant="contained"
                        startIcon={<AssignmentTurnedInIcon />}
                        disabled={selected.length === 0 || loading}
                        onClick={handleMassAuditClick}
                        sx={{ backgroundColor: selected.length > 0 ? '#4CAF50' : undefined, color: 'white', '&:hover': { backgroundColor: '#388E3C' } }}
                        size="small"
                    >
                        Auditar Seleccionados
                    </Button>
                </Box>
            </Paper>
            {/* Data Table */}
            <TableContainer component={Paper} sx={{ maxHeight: 600, width: '100%', overflowX: 'auto' }}>
                <Table stickyHeader size="small" sx={{ width: '100%' }}>
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox" sx={{ width: 60, minWidth: 60, pl: 2, pr: 1 }}>
                                <Checkbox
                                    indeterminate={selected.length > 0 && selected.length < data.length}
                                    checked={data.length > 0 && selected.length === data.length}
                                    onChange={handleSelectAll}
                                />
                            </TableCell>
                            <TableCell>Estado</TableCell>
                            <TableCell align="center">
                                <Tooltip title="Estado en Conciliación Externa (BSJ/RA)"><span>Conciliación</span></Tooltip>
                            </TableCell>
                            <TableCell align="center" sx={{ width: 30 }}>
                                <Tooltip title="Resultado cruzamiento"><SyncIcon fontSize="small" /></Tooltip>
                            </TableCell>
                            <TableCell>Abogado</TableCell>
                            <TableCell>Autos</TableCell>
                            <TableCell>Juzgado</TableCell>
                            <TableCell>Nro. Plan</TableCell>
                            <TableCell>Fecha</TableCell>
                            <TableCell>Serie/Certif</TableCell>
                            <TableCell align="right">Deuda</TableCell>
                            <TableCell align="right">Costas</TableCell>
                            <TableCell align="center">Acciones</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={13} align="center" style={{ height: 200 }}>
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : (data.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={13} align="center">
                                    No se encontraron registros.
                                </TableCell>
                            </TableRow>
                        ) : (
                            data
                                .filter(row => {
                                    if (filtroCruzamiento === 'coincide') return row.resultado_cruzamiento === 1;
                                    if (filtroCruzamiento === 'sin_match') return row.resultado_cruzamiento === 0;
                                    if (filtroCruzamiento === 'sin_cruzar') return row.resultado_cruzamiento === -1;
                                    return true;
                                })
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((row, idx) => {
                                    const isSelected = selected.indexOf(row.id) !== -1;
                                    return (
                                        <TableRow
                                            key={row.id}
                                            hover
                                            role="checkbox"
                                            aria-checked={isSelected}
                                            selected={isSelected}
                                            sx={{ backgroundColor: idx % 2 === 0 ? "rgba(0,0,0,0.02)" : "transparent" }}
                                        >
                                            <TableCell padding="checkbox" sx={{ width: 60, minWidth: 60, pl: 2, pr: 1 }}>
                                                <Checkbox
                                                    checked={isSelected}
                                                    onClick={(event) => handleSelect(event, row.id)}
                                                    disabled={row.auditado === 1}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
                                                {row.auditado === 1 && <Chip label="Auditado" size="small" sx={{ backgroundColor: '#E8F5E9', color: '#2E7D32', fontWeight: 'bold' }} icon={<CheckCircleIcon />} />}
                                                {row.auditado === 0 && <Chip label="Pendiente" size="small" sx={{ backgroundColor: '#EEEEEE', color: '#616161' }} />}
                                                {row.auditado === -1 && <Chip label="Incomp." size="small" sx={{ backgroundColor: '#FFF3E0', color: '#EF6C00', fontWeight: 'bold' }} icon={<WarningIcon />} />}
                                            </TableCell>
                                            <TableCell align="center">
                                                {(() => {
                                                    const ce = row.conciliacion_estado;
                                                    if (!ce) {
                                                        return <Tooltip title="Aún no se concilió este plan en BSJ/RA"><Chip label="—" size="small" variant="outlined" sx={{ color: '#999' }} /></Tooltip>;
                                                    }
                                                    const labels = { V: 'Conciliado', X: 'Sin Coinc.', S: 'Sin Pagos', W: 'Sin Solicitud', D: 'Duplicado' };
                                                    const colors = { V: { bg: '#E8F5E9', fg: '#2E7D32' }, X: { bg: '#FFEBEE', fg: '#C62828' }, S: { bg: '#FFF3E0', fg: '#E65100' }, W: { bg: '#E3F2FD', fg: '#0277BD' }, D: { bg: '#EEEEEE', fg: '#616161' } };
                                                    const c = colors[ce] || colors.D;
                                                    const origenLbl = row.conciliacion_origen
                                                        ? `${row.conciliacion_origen}${row.conciliacion_suborigen ? ' / ' + row.conciliacion_suborigen : ''}`
                                                        : '';
                                                    const tt = `${labels[ce] || ce}${origenLbl ? ' · Origen: ' + origenLbl : ''}` +
                                                        (Number(row.bsj_honorarios) > 0 ? ` · BSJ Hon: $${Number(row.bsj_honorarios).toLocaleString('es-AR', {minimumFractionDigits:2,maximumFractionDigits:2})}` : '') +
                                                        (Number(row.ra_honorarios) > 0 ? ` · RA Hon: $${Number(row.ra_honorarios).toLocaleString('es-AR', {minimumFractionDigits:2,maximumFractionDigits:2})}` : '');
                                                    return (
                                                        <Tooltip title={tt}>
                                                            <Chip label={labels[ce] || ce} size="small" sx={{ backgroundColor: c.bg, color: c.fg, fontWeight: 600 }} />
                                                        </Tooltip>
                                                    );
                                                })()}
                                            </TableCell>
                                            <TableCell align="center">
                                                <Tooltip title={
                                                    row.resultado_cruzamiento === 1 ? 'Coincide con certificado interno' :
                                                    row.resultado_cruzamiento === 0 ? 'Sin coincidencia' : 'Sin cruzar'
                                                }>
                                                    <FiberManualRecordIcon sx={{
                                                        fontSize: 14,
                                                        color: row.resultado_cruzamiento === 1 ? '#4CAF50' :
                                                               row.resultado_cruzamiento === 0 ? '#f44336' : '#FF9800'
                                                    }} />
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell sx={{ wordBreak: 'break-word', whiteSpace: 'normal', fontSize: '0.8rem' }}>{row.abogado}{row.matricula ? ` (${row.matricula})` : ''}</TableCell>
                                            <TableCell sx={{ wordBreak: 'break-word', whiteSpace: 'normal', fontSize: '0.8rem' }}>{row.autos}</TableCell>
                                            <TableCell sx={{ wordBreak: 'break-word', whiteSpace: 'normal', fontSize: '0.75rem', maxWidth: 200 }}>{row.radicacion}</TableCell>
                                            <TableCell sx={{ wordBreak: 'break-word', whiteSpace: 'normal', fontSize: '0.8rem' }}>{row.ref_nro_plan}</TableCell>
                                            <TableCell sx={{ wordBreak: 'break-word', whiteSpace: 'normal', fontSize: '0.8rem' }}>{row.fecha_pago}</TableCell>
                                            <TableCell sx={{ wordBreak: 'break-word', whiteSpace: 'normal', fontSize: '0.8rem' }}>
                                                {(() => { const numero = String(row.certificado || "").replace(/\s+/g, ""); const serie = (row.serie || "").trim(); return serie ? `${serie}-${numero}` : numero; })()}
                                            </TableCell>
                                            <TableCell sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }} align="right">${row.deuda.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</TableCell>
                                            <TableCell sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }} align="right">${row.costas.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</TableCell>
                                            <TableCell align="center">
                                                <Tooltip title="Detalle del Plan (ciclo completo)">
                                                    <IconButton size="small" onClick={() => setPlanDetalleId(row.id)}>
                                                        <LayersIcon fontSize="small" sx={{ color: '#1565c0' }} />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Ver Expediente">
                                                    <IconButton size="small" onClick={() => openExpediente(row)}>
                                                        <VisibilityIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Observaciones">
                                                    <IconButton size="small" onClick={() => openObs(row)}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[50, 100, 200]}
                component="div"
                count={data.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                }}
            />

            {/* Super-drawer reusable: detalle completo del ciclo del plan */}
            <DetallePlanDrawer
                open={!!planDetalleId}
                idDetalle={planDetalleId}
                onClose={() => setPlanDetalleId(null)}
            />
        </Box>
    );
}
